import type { Article } from "../insights";
import { ctaPl, ctaEn, ctaZh } from "../insight-cta";

export const kosztRekrutacjiItPolska: Article = {
  slug: "koszt-rekrutacji-it-polska",
  updated: "2026-09-08",
  updatedLabel: {
    pl: "Aktualizacja: 8 września 2026",
    en: "Last updated: 8 September 2026",
    zh: "最近更新：2026 年 9 月 8 日",
  },
  topic: { pl: "Koszty", en: "Cost", zh: "招聘成本" },
  pl: {
    title: "Ile kosztuje rekrutacja IT w Polsce",
    h1: "Koszt rekrutacji IT w Polsce 2026: pełne wyliczenie",
    metaTitle: "Koszt rekrutacji IT w Polsce 2026: prowizje i cena wakatu",
    metaDescription:
      "Ile kosztuje rekrutacja programisty w Polsce w 2026: prowizje agencji, koszt własnego zespołu, cena nieobsadzonego wakatu i koszt nietrafionego zatrudnienia.",
    eyebrow: "Insights · Koszty",
    summary:
      "Pełny rachunek jednego zatrudnienia w IT: prowizja, koszt wewnętrzny, cena wakatu i koszt pomyłki.",
    lead:
      "Większość firm liczy koszt rekrutacji jako fakturę od agencji. To najmniejsza część rachunku. Prowadzimy rekrutację IT z Krakowa na całą Polskę i region CEE, więc poniżej rozkładamy na czynniki pierwsze wszystko, co realnie płacisz przy jednym zatrudnieniu: czas własnego zespołu, koszt każdego tygodnia pustego biurka i cenę decyzji, która nie wypaliła.",
    note:
      "Kwoty są w PLN i dotyczą polskiego rynku IT w 2026. Prowizje agencyjne podajemy jako procent rocznego wynagrodzenia brutto kandydata. Traktuj liczby jako zakresy planistyczne, nie oferty.",
    readTime: "9 min czytania",
    takeaways: [
      "Prowizja agencji w Polsce to zwykle 15-22% rocznego wynagrodzenia brutto, czyli 35 000 - 90 000 PLN przy rolach senior.",
      "Rekrutacja własnymi siłami nie jest darmowa: jedno zatrudnienie seniora pochłania 40-70 godzin pracy rekrutera i zespołu technicznego.",
      "Nieobsadzony wakat kosztuje 15 000 - 45 000 PLN miesięcznie w utraconej pracy zespołu i opóźnionych wdrożeniach.",
      "Nietrafione zatrudnienie kosztuje 3-6 miesięcznych wynagrodzeń, licząc wdrożenie, odprawę i powtórzoną rekrutację.",
      "Skrócenie procesu o dwa tygodnie zwykle oszczędza więcej niż wynegocjowanie dwóch punktów procentowych prowizji.",
      "Nasz Fit-to-Win Sprint domyka krótką listę w 14 dni, co bezpośrednio obcina największą pozycję kosztową: czas pustego biurka.",
    ],
    sections: [
      {
        id: "prowizje",
        eyebrow: "01 / Prowizje",
        title: "Ile kosztuje agencja rekrutacyjna IT w Polsce",
        blocks: [
          {
            kind: "table",
            caption: "Prowizja przy różnych poziomach wynagrodzenia, Polska 2026",
            head: ["Rola", "Wynagrodzenie roczne brutto", "Prowizja 15%", "Prowizja 20%"],
            rows: [
              ["Mid Developer", "180 000 - 240 000 PLN", "27 000 - 36 000 PLN", "36 000 - 48 000 PLN"],
              ["Senior Developer", "260 000 - 340 000 PLN", "39 000 - 51 000 PLN", "52 000 - 68 000 PLN"],
              ["Tech Lead / Architekt", "340 000 - 450 000 PLN", "51 000 - 67 500 PLN", "68 000 - 90 000 PLN"],
              ["Senior AI / ML Engineer", "330 000 - 480 000 PLN", "49 500 - 72 000 PLN", "66 000 - 96 000 PLN"],
              ["Head of Engineering", "450 000 - 650 000 PLN", "67 500 - 97 500 PLN", "90 000 - 130 000 PLN"],
            ],
          },
          {
            kind: "p",
            text: "Stawka to nie cała historia. Liczy się też moment płatności (po zatrudnieniu czy w ratach), długość gwarancji zastąpienia (rynkowy standard to 60-90 dni) oraz to, czy prowizja liczona jest od wynagrodzenia zasadniczego, czy razem z premiami. Dwie oferty z tym samym procentem potrafią różnić się o kilkanaście tysięcy złotych.",
          },
        ],
      },
      {
        id: "wewnetrznie",
        eyebrow: "02 / Własnymi siłami",
        title: "Ile realnie kosztuje rekrutacja prowadzona wewnętrznie",
        blocks: [
          {
            kind: "table",
            caption: "Nakład pracy przy jednym zatrudnieniu seniora IT",
            head: ["Etap", "Godziny", "Kto", "Szacunkowy koszt"],
            rows: [
              ["Brief, opis roli, kalibracja widełek", "4 - 6 h", "Rekruter + hiring manager", "800 - 1 500 PLN"],
              ["Sourcing i wiadomości do kandydatów", "15 - 25 h", "Rekruter", "2 300 - 4 000 PLN"],
              ["Rozmowy wstępne", "8 - 12 h", "Rekruter", "1 200 - 1 900 PLN"],
              ["Weryfikacja techniczna", "10 - 18 h", "Inżynierowie", "3 500 - 6 500 PLN"],
              ["Decyzje, oferta, negocjacje", "3 - 6 h", "Hiring manager", "900 - 1 800 PLN"],
              ["Razem", "40 - 70 h", "-", "8 700 - 15 700 PLN"],
            ],
          },
          {
            kind: "p",
            text: "Do tego dochodzą narzędzia: ogłoszenia na portalach pracy (1 500 - 4 000 PLN za rolę), licencje na LinkedIn Recruiter (ok. 40 000 PLN rocznie za stanowisko) i system ATS. Rekrutacja wewnętrzna opłaca się przy stałym wolumenie i powtarzalnych profilach. Przy jednej trudnej roli rocznie sam koszt narzędzi przewyższa prowizję agencji.",
          },
        ],
      },
      {
        id: "wakat",
        eyebrow: "03 / Cena wakatu",
        title: "Ile kosztuje każdy tydzień nieobsadzonego stanowiska",
        blocks: [
          {
            kind: "table",
            caption: "Miesięczny koszt pustego biurka w zespole inżynierskim",
            head: ["Składnik", "Koszt miesięczny", "Skąd się bierze"],
            rows: [
              ["Niedostarczona praca", "20 000 - 35 000 PLN", "Wartość pracy, której nikt nie wykonuje"],
              ["Przeciążenie zespołu", "5 000 - 12 000 PLN", "Nadgodziny, spadek jakości, dług techniczny"],
              ["Opóźnienie wdrożeń", "zmienne", "Przesunięty przychód lub kara umowna w projekcie"],
              ["Ryzyko odejść", "trudne do wyceny", "Wypalenie osób przejmujących obowiązki"],
              ["Realny zakres netto", "15 000 - 45 000 PLN", "Zależnie od roli i krytyczności projektu"],
            ],
          },
          {
            kind: "p",
            text: "Ta jedna tabela zmienia zwykle całą matematykę. Jeśli wakat kosztuje 30 000 PLN miesięcznie, to skrócenie procesu z dwunastu tygodni do pięciu oszczędza około 50 000 PLN, czyli więcej niż cała prowizja za tę rekrutację. Dlatego negocjowanie procentu kosztem tempa najczęściej się nie opłaca.",
          },
        ],
      },
      {
        id: "pomylka",
        eyebrow: "04 / Koszt pomyłki",
        title: "Co kosztuje nietrafione zatrudnienie",
        blocks: [
          {
            kind: "cards",
            items: [
              {
                title: "Wynagrodzenie za okres próby",
                meta: "3 - 6 miesięcy",
                body: "Pełna pensja wypłacana za okres, w którym efekt pracy nie pokrył kosztu. Przy seniorze to zwykle 60 000 - 150 000 PLN.",
              },
              {
                title: "Czas zespołu na wdrożenie",
                meta: "40 - 80 godzin",
                body: "Onboarding, przeglądy kodu, mentoring, które przepadają razem z odejściem osoby.",
              },
              {
                title: "Powtórzona rekrutacja",
                meta: "Drugi pełny cykl",
                body: "Ponowna prowizja (o ile minęła gwarancja), ponowny czas zespołu, ponowny koszt wakatu.",
              },
              {
                title: "Efekt na zespół",
                meta: "Najdroższy, niewidoczny w Excelu",
                body: "Spadek zaufania do procesu rekrutacji i ryzyko, że sfrustrowani członkowie zespołu zaczną rozglądać się za pracą.",
              },
            ],
          },
          {
            kind: "p",
            text: "Dlatego gwarancja zastąpienia i rzetelny screening są ważniejsze od najniższej stawki. Partner, który przedstawia sześciu dopasowanych kandydatów zamiast dwudziestu przypadkowych, obniża prawdopodobieństwo najdroższej pozycji na tej liście.",
          },
        ],
      },
      {
        id: "jak-obnizyc",
        eyebrow: "05 / Optymalizacja",
        title: "Sześć rzeczy, które realnie obniżają koszt rekrutacji IT",
        blocks: [
          {
            kind: "list",
            items: [
              "Skróć proces do maksymalnie trzech etapów i trzymaj maksymalnie 5 dni roboczych między nimi - każdy dodatkowy tydzień to utracone finały i koszt wakatu.",
              "Podaj widełki w ogłoszeniu i w pierwszej wiadomości - odsiewa niedopasowanie zanim zajmie czas zespołowi technicznemu.",
              "Rozdziel must-have od nice-to-have; lista dwudziestu wymagań zawęża pulę o kilkadziesiąt procent i podnosi oczekiwania płacowe.",
              "Zdecyduj o modelu pracy przed startem - wymóg pełnej obecności w biurze zwykle zawęża pulę kandydatów o połowę.",
              "Ustal jednego decydenta; procesy z rozmytą decyzyjnością gubią finalistów na ostatniej prostej.",
              "Przy kilku rolach naraz policz model RPO lub subskrypcyjny zamiast pojedynczych prowizji - przy trzech i więcej zatrudnieniach zwykle wychodzi taniej.",
            ],
          },
          {
            kind: "p",
            text: "Wszystkie sześć punktów sprowadza się do jednego: koszt rekrutacji IT jest głównie funkcją czasu, nie stawki. Firmy, które decydują szybko, płacą mniej za to samo zatrudnienie.",
          },
        ],
      },
    ],
    faqTitle: "Najczęstsze pytania",
    faq: [
      {
        q: "Ile kosztuje rekrutacja programisty przez agencję w Polsce?",
        a: "Standardowa prowizja to 15-22% rocznego wynagrodzenia brutto kandydata. Dla seniora zarabiającego 25 000 PLN brutto miesięcznie oznacza to około 45 000 - 66 000 PLN, zwykle z gwarancją zastąpienia na 60-90 dni.",
      },
      {
        q: "Czy taniej jest rekrutować własnym zespołem?",
        a: "Przy stałym wolumenie i powtarzalnych profilach - tak. Przy pojedynczych, trudnych rolach rzadko, bo 40-70 godzin pracy zespołu plus narzędzia (ogłoszenia, LinkedIn Recruiter, ATS) zwykle przekraczają prowizję, a proces trwa dłużej.",
      },
      {
        q: "Ile kosztuje nieobsadzony wakat w zespole IT?",
        a: "Zwykle 15 000 - 45 000 PLN miesięcznie: niedostarczona praca, przeciążenie zespołu i opóźnione wdrożenia. To najczęściej pomijana i jednocześnie największa pozycja w rachunku rekrutacji.",
      },
      {
        q: "Co składa się na koszt nietrafionego zatrudnienia?",
        a: "Wynagrodzenie za 3-6 miesięcy, czas zespołu poświęcony na wdrożenie, powtórzona rekrutacja i efekt na morale. W praktyce to równowartość 3-6 miesięcznych wynagrodzeń na tym stanowisku.",
      },
      {
        q: "Kiedy opłaca się model RPO zamiast prowizji za zatrudnienie?",
        a: "Zwykle od trzech równoległych rekrutacji lub przy stałym planie zatrudnień na kilka miesięcy. Stała opłata za pojemność procesu jest wtedy niższa niż suma pojedynczych prowizji.",
      },
    ],
    cta: ctaPl,
    methodology:
      "Metodologia: wyliczenia opierają się na procesach rekrutacyjnych prowadzonych przez Positive Recruitment w latach 2025-2026 oraz na publicznych raportach płacowych polskiego rynku IT. Koszty godzinowe zespołu policzono na podstawie średnich stawek rekruterów i inżynierów w Polsce. Liczby są zakresami planistycznymi, nie gwarancjami.",
  },
  en: {
    title: "What IT recruitment costs in Poland",
    h1: "The cost of IT recruitment in Poland 2026: full breakdown",
    metaTitle: "Cost of IT Recruitment in Poland 2026: Fees and Vacancy Cost",
    metaDescription:
      "What hiring a developer in Poland really costs in 2026: agency fees, in-house effort, the monthly price of an open vacancy and the cost of a bad hire.",
    eyebrow: "Insights · Cost",
    summary:
      "The full invoice for one tech hire: agency fee, internal effort, vacancy cost and the price of getting it wrong.",
    lead:
      "Most companies measure recruitment cost as the agency invoice. That is the smallest line on the bill. We run IT recruitment from Kraków across Poland and CEE, so below we break down everything you actually pay for a single hire: your own team's hours, the price of every week an empty desk stays empty, and what a hire that does not work out really costs.",
    note:
      "Figures are in PLN for the Polish IT market in 2026. Agency fees are shown as a percentage of the candidate's gross annual salary. Treat the numbers as planning ranges, not quotes.",
    readTime: "9 min read",
    takeaways: [
      "Agency fees in Poland run 15-22% of gross annual salary, i.e. PLN 35,000-90,000 for senior roles.",
      "In-house hiring is not free: one senior hire absorbs 40-70 hours of recruiter and engineering time.",
      "An open vacancy costs PLN 15,000-45,000 per month in undelivered work and delayed releases.",
      "A bad hire costs the equivalent of 3-6 monthly salaries once onboarding, exit and a repeat search are counted.",
      "Cutting two weeks off the process usually saves more than negotiating two percentage points off the fee.",
      "Our Fit-to-Win Sprint closes a shortlist in 14 days, which attacks the largest cost line directly: empty-desk time.",
    ],
    sections: [
      {
        id: "prowizje",
        eyebrow: "01 / Fees",
        title: "What an IT recruitment agency costs in Poland",
        blocks: [
          {
            kind: "table",
            caption: "Fee by salary level, Poland 2026",
            head: ["Role", "Gross annual salary", "Fee at 15%", "Fee at 20%"],
            rows: [
              ["Mid developer", "PLN 180,000 - 240,000", "PLN 27,000 - 36,000", "PLN 36,000 - 48,000"],
              ["Senior developer", "PLN 260,000 - 340,000", "PLN 39,000 - 51,000", "PLN 52,000 - 68,000"],
              ["Tech lead / architect", "PLN 340,000 - 450,000", "PLN 51,000 - 67,500", "PLN 68,000 - 90,000"],
              ["Senior AI / ML engineer", "PLN 330,000 - 480,000", "PLN 49,500 - 72,000", "PLN 66,000 - 96,000"],
              ["Head of engineering", "PLN 450,000 - 650,000", "PLN 67,500 - 97,500", "PLN 90,000 - 130,000"],
            ],
          },
          {
            kind: "p",
            text: "The percentage is not the whole story. Payment timing (on hire or in instalments), the length of the replacement guarantee (60-90 days is the market standard) and whether the fee is calculated on base salary or on total compensation can move two identically priced offers apart by tens of thousands of złoty.",
          },
        ],
      },
      {
        id: "wewnetrznie",
        eyebrow: "02 / In-house",
        title: "What running the search yourself actually costs",
        blocks: [
          {
            kind: "table",
            caption: "Effort behind one senior IT hire",
            head: ["Stage", "Hours", "Who", "Estimated cost"],
            rows: [
              ["Brief, role scope, salary calibration", "4 - 6 h", "Recruiter + hiring manager", "PLN 800 - 1,500"],
              ["Sourcing and outreach", "15 - 25 h", "Recruiter", "PLN 2,300 - 4,000"],
              ["Screening calls", "8 - 12 h", "Recruiter", "PLN 1,200 - 1,900"],
              ["Technical assessment", "10 - 18 h", "Engineers", "PLN 3,500 - 6,500"],
              ["Decisions, offer, negotiation", "3 - 6 h", "Hiring manager", "PLN 900 - 1,800"],
              ["Total", "40 - 70 h", "-", "PLN 8,700 - 15,700"],
            ],
          },
          {
            kind: "p",
            text: "Tooling sits on top: job board postings (PLN 1,500-4,000 per role), a LinkedIn Recruiter seat (around PLN 40,000 per year) and an ATS. In-house recruiting wins on steady volume and repeatable profiles. For one hard role a year, tooling alone costs more than an agency fee.",
          },
        ],
      },
      {
        id: "wakat",
        eyebrow: "03 / Vacancy cost",
        title: "What every week of an open role costs",
        blocks: [
          {
            kind: "table",
            caption: "Monthly cost of an empty seat in an engineering team",
            head: ["Component", "Monthly cost", "Where it comes from"],
            rows: [
              ["Undelivered work", "PLN 20,000 - 35,000", "Output nobody is producing"],
              ["Team overload", "PLN 5,000 - 12,000", "Overtime, quality drop, technical debt"],
              ["Delayed releases", "Variable", "Deferred revenue or contractual penalties"],
              ["Attrition risk", "Hard to price", "Burnout among the people covering the gap"],
              ["Realistic net range", "PLN 15,000 - 45,000", "Depending on role and project criticality"],
            ],
          },
          {
            kind: "p",
            text: "This single table usually rewrites the maths. If a vacancy costs PLN 30,000 a month, cutting the process from twelve weeks to five saves roughly PLN 50,000, more than the entire agency fee. Negotiating the percentage at the expense of speed rarely pays.",
          },
        ],
      },
      {
        id: "pomylka",
        eyebrow: "04 / Bad hire",
        title: "What a hire that does not work out costs",
        blocks: [
          {
            kind: "cards",
            items: [
              {
                title: "Salary during the trial period",
                meta: "3 - 6 months",
                body: "Full pay for a period where output did not cover cost. For a senior that is typically PLN 60,000-150,000.",
              },
              {
                title: "Team onboarding time",
                meta: "40 - 80 hours",
                body: "Onboarding, code reviews and mentoring that walk out of the door with the person.",
              },
              {
                title: "Repeating the search",
                meta: "A second full cycle",
                body: "Another fee (once the guarantee has lapsed), another round of team time, another stretch of vacancy cost.",
              },
              {
                title: "Effect on the team",
                meta: "The priciest line, invisible in the spreadsheet",
                body: "Lost trust in the hiring process and the risk that frustrated engineers start looking themselves.",
              },
            ],
          },
          {
            kind: "p",
            text: "That is why a replacement guarantee and serious screening matter more than the lowest rate. A partner who presents six well-matched candidates instead of twenty random ones lowers the odds of the most expensive item on this list.",
          },
        ],
      },
      {
        id: "jak-obnizyc",
        eyebrow: "05 / Optimisation",
        title: "Six things that genuinely lower IT hiring cost",
        blocks: [
          {
            kind: "list",
            items: [
              "Cap the process at three stages and five working days between them - every extra week loses finalists and adds vacancy cost.",
              "Publish the range in the ad and the first message - it filters mismatches before they consume engineering time.",
              "Separate must-haves from nice-to-haves; a twenty-item requirement list shrinks the pool and raises salary expectations.",
              "Settle the work model before you start - a full onsite requirement typically halves the available pool.",
              "Name one decision-maker; processes with diffuse authority lose finalists at the final step.",
              "For several roles at once, price an RPO or subscription model instead of individual fees - from three hires up it is usually cheaper.",
            ],
          },
          {
            kind: "p",
            text: "All six come down to one point: IT recruitment cost is mostly a function of time, not rate. Companies that decide quickly pay less for the same hire.",
          },
        ],
      },
    ],
    faqTitle: "Frequently asked questions",
    faq: [
      {
        q: "How much does it cost to hire a developer through an agency in Poland?",
        a: "The standard fee is 15-22% of the candidate's gross annual salary. For a senior earning PLN 25,000 gross per month that is roughly PLN 45,000-66,000, usually with a 60-90 day replacement guarantee.",
      },
      {
        q: "Is hiring in-house cheaper?",
        a: "With steady volume and repeatable profiles, yes. For single hard roles rarely, because 40-70 hours of team time plus tooling (job ads, LinkedIn Recruiter, ATS) usually exceeds the fee and the process takes longer.",
      },
      {
        q: "What does an unfilled IT vacancy cost?",
        a: "Typically PLN 15,000-45,000 per month in undelivered work, team overload and delayed releases. It is the most commonly ignored and the largest line in the recruitment bill.",
      },
      {
        q: "What makes up the cost of a bad hire?",
        a: "Three to six months of salary, the team's onboarding time, a repeated search and the effect on morale. In practice, the equivalent of 3-6 monthly salaries for that role.",
      },
      {
        q: "When does RPO beat a per-hire fee?",
        a: "Usually from three parallel searches, or with a hiring plan spanning several months. A fixed capacity fee then comes in below the sum of individual placement fees.",
      },
    ],
    cta: ctaEn,
    methodology:
      "Methodology: calculations are based on searches run by Positive Recruitment in 2025-2026 and on public Polish IT salary reports. Hourly team costs use average recruiter and engineer rates in Poland. All figures are planning ranges, not guarantees.",
  },
  zh: {
    title: "在波兰招聘IT人才的成本",
    h1: "2026年波兰IT招聘成本全解析",
    metaTitle: "2026波兰IT招聘成本: 中介费与空缺岗位成本",
    metaDescription:
      "2026年在波兰招聘工程师的真实成本: 中介佣金、自建团队投入、岗位空缺的每月损失以及错误招聘的代价。",
    eyebrow: "Insights · 招聘成本",
    summary: "一次技术招聘的完整账单: 佣金、内部投入、空缺成本与错误招聘的代价。",
    lead:
      "多数企业把招聘成本等同于中介发票，而这恰恰是账单中最小的一项。我们从克拉科夫出发，服务波兰及中东欧市场。以下拆解一次招聘中你真正支付的全部成本: 内部团队工时、岗位空置每周的代价，以及一次失败录用的真实价格。",
    note: "金额以波兰兹罗提(PLN)计，适用于2026年波兰IT市场。中介佣金按候选人年度税前工资的百分比表示。数字为规划区间，不构成报价。",
    readTime: "9 分钟阅读",
    takeaways: [
      "波兰中介佣金通常为年度税前工资的15-22%，资深岗位约合 35,000-90,000 PLN。",
      "自主招聘并非免费: 一次资深岗位招聘需要招聘人员与工程师 40-70 小时。",
      "岗位空缺每月造成 15,000-45,000 PLN 的产出与交付损失。",
      "一次错误录用的代价约为该岗位 3-6 个月工资。",
      "把流程缩短两周，通常比把佣金压低两个百分点更省钱。",
      "我们的 Fit-to-Win Sprint 在 14 天内交付候选名单，直接压缩最大的成本项: 空缺时间。",
    ],
    sections: [
      {
        id: "prowizje",
        eyebrow: "01 / 佣金",
        title: "波兰IT招聘中介的收费水平",
        blocks: [
          {
            kind: "table",
            caption: "不同薪资水平对应的佣金, 波兰 2026",
            head: ["岗位", "年度税前工资", "15% 佣金", "20% 佣金"],
            rows: [
              ["中级开发", "180,000 - 240,000 PLN", "27,000 - 36,000 PLN", "36,000 - 48,000 PLN"],
              ["资深开发", "260,000 - 340,000 PLN", "39,000 - 51,000 PLN", "52,000 - 68,000 PLN"],
              ["技术负责人 / 架构师", "340,000 - 450,000 PLN", "51,000 - 67,500 PLN", "68,000 - 90,000 PLN"],
              ["资深 AI / ML 工程师", "330,000 - 480,000 PLN", "49,500 - 72,000 PLN", "66,000 - 96,000 PLN"],
              ["工程负责人", "450,000 - 650,000 PLN", "67,500 - 97,500 PLN", "90,000 - 130,000 PLN"],
            ],
          },
          {
            kind: "p",
            text: "百分比之外还要看付款节点、替换保证期(市场惯例为60-90天)以及佣金基数是基本工资还是含奖金的总薪酬。同样的百分比，最终差额可能达到数万兹罗提。",
          },
        ],
      },
      {
        id: "wewnetrznie",
        eyebrow: "02 / 自主招聘",
        title: "内部招聘的真实成本",
        blocks: [
          {
            kind: "table",
            caption: "一次资深IT招聘的工时投入",
            head: ["环节", "工时", "参与者", "估算成本"],
            rows: [
              ["岗位梳理与薪资校准", "4 - 6 小时", "招聘 + 用人经理", "800 - 1,500 PLN"],
              ["寻访与触达", "15 - 25 小时", "招聘", "2,300 - 4,000 PLN"],
              ["初筛沟通", "8 - 12 小时", "招聘", "1,200 - 1,900 PLN"],
              ["技术评估", "10 - 18 小时", "工程师", "3,500 - 6,500 PLN"],
              ["决策与offer谈判", "3 - 6 小时", "用人经理", "900 - 1,800 PLN"],
              ["合计", "40 - 70 小时", "-", "8,700 - 15,700 PLN"],
            ],
          },
          {
            kind: "p",
            text: "此外还有工具成本: 招聘网站职位发布(每个岗位1,500-4,000 PLN)、LinkedIn Recruiter 席位(每年约40,000 PLN)以及ATS系统。稳定的招聘量适合自建团队; 一年只招一个难岗位时，仅工具成本就超过中介佣金。",
          },
        ],
      },
      {
        id: "wakat",
        eyebrow: "03 / 空缺成本",
        title: "岗位空置每周的代价",
        blocks: [
          {
            kind: "table",
            caption: "工程团队岗位空缺的月度成本",
            head: ["构成", "每月成本", "来源"],
            rows: [
              ["未完成的产出", "20,000 - 35,000 PLN", "无人承担的工作量"],
              ["团队超负荷", "5,000 - 12,000 PLN", "加班、质量下降、技术债"],
              ["交付延期", "浮动", "收入递延或合同罚则"],
              ["离职风险", "难以估价", "顶班同事的倦怠"],
              ["实际净区间", "15,000 - 45,000 PLN", "取决于岗位与项目关键性"],
            ],
          },
          {
            kind: "p",
            text: "这张表通常会改写整笔账。若空缺每月成本为30,000 PLN，把流程从12周压缩到5周即可节省约50,000 PLN，高于全部中介佣金。为压价而牺牲速度往往得不偿失。",
          },
        ],
      },
      {
        id: "pomylka",
        eyebrow: "04 / 错误录用",
        title: "一次失败录用的代价",
        blocks: [
          {
            kind: "cards",
            items: [
              { title: "试用期工资", meta: "3 - 6 个月", body: "产出未覆盖成本期间的全额薪资，资深岗位通常为 60,000-150,000 PLN。" },
              { title: "团队带教时间", meta: "40 - 80 小时", body: "入职引导、代码评审与辅导，随人员离开而清零。" },
              { title: "重新招聘", meta: "第二个完整周期", body: "再次支付佣金(若保证期已过)、再次投入团队时间、再次承担空缺成本。" },
              { title: "对团队的影响", meta: "最贵且难以量化", body: "团队对招聘流程失去信任，受挫的工程师可能开始寻找新机会。" },
            ],
          },
          {
            kind: "p",
            text: "因此替换保证与严谨筛选比最低报价更重要。提供六位高度匹配的候选人，胜过二十份随机简历。",
          },
        ],
      },
      {
        id: "jak-obnizyc",
        eyebrow: "05 / 优化",
        title: "真正能降低招聘成本的六件事",
        blocks: [
          {
            kind: "list",
            items: [
              "流程不超过三轮，各环节间隔不超过5个工作日。",
              "在职位与首封触达信息中公开薪资区间。",
              "区分必备与加分项，避免二十条硬性要求。",
              "开始前确定办公模式; 强制全职到岗通常使候选池减半。",
              "指定唯一决策人，避免在最后一步流失终选者。",
              "同时招多个岗位时，评估 RPO 或订阅制，通常比逐个付佣金更划算。",
            ],
          },
          {
            kind: "p",
            text: "六条归结为一点: IT招聘成本主要取决于时间，而非费率。决策快的企业，为同一位人才付出的总成本更低。",
          },
        ],
      },
    ],
    faqTitle: "常见问题",
    faq: [
      { q: "在波兰通过中介招聘工程师要花多少钱?", a: "标准佣金为候选人年度税前工资的15-22%。月薪25,000 PLN的资深工程师约合 45,000-66,000 PLN，通常含60-90天替换保证。" },
      { q: "自主招聘更便宜吗?", a: "招聘量稳定且岗位可复制时更划算; 单个高难度岗位通常不划算，因为40-70小时团队投入加上工具成本往往超过佣金，且周期更长。" },
      { q: "岗位空缺的成本是多少?", a: "通常每月 15,000-45,000 PLN，包括未完成的产出、团队超负荷与交付延期。" },
      { q: "错误录用的成本由哪些部分构成?", a: "3-6个月工资、团队带教时间、重新招聘以及对士气的影响，合计约为该岗位3-6个月薪资。" },
      { q: "什么时候适合选择 RPO 而非按次付费?", a: "通常从三个并行岗位开始，或存在数月的持续招聘计划时。" },
    ],
    cta: ctaZh,
    methodology:
      "方法说明: 数据基于 Positive Recruitment 于2025-2026年在波兰开展的招聘流程，并结合公开薪酬报告。团队工时成本按波兰招聘人员与工程师的平均费率估算，均为规划区间。",
  },
};
